<template>
  <div class="site-editor">
    <h3 class="site-editor__title">Website Builder</h3>
    <p class="site-editor__lead">
      Hele nettstedet ditt: hjemmesiden gjestene kommer til, og bestillingssiden de bestiller fra.
    </p>

    <div class="site-editor__tabs" role="tablist">
      <button
          type="button"
          role="tab"
          :aria-selected="tab === 'home'"
          :class="{ active: tab === 'home' }"
          @click="go('home')">
        <font-awesome-icon :icon="['fas', 'home']" /> Hjemmeside
      </button>
      <button
          type="button"
          role="tab"
          :aria-selected="tab === 'ordering'"
          :class="{ active: tab === 'ordering' }"
          @click="go('ordering')">
        <font-awesome-icon :icon="['fas', 'shopping-basket']" /> Bestillingsside
      </button>
    </div>

    <p class="site-editor__note">
      <template v-if="tab === 'home'">
        Forsiden gjestene kommer til. Farger og skrift du velger her brukes på hele nettstedet, også
        bestillingssiden.
      </template>
      <template v-else>
        Nettsiden der gjestene bestiller. Utseendet arves fra
        <a href="#" @click.prevent="go('home')">Hjemmeside</a> — her styrer du hvordan menyen vises.
      </template>
    </p>

    <site-home v-if="tab === 'home'" @published="orderingReload++" />
    <site-theme v-else :key="orderingReload" />
  </div>
</template>

<script>
/**
 * Website Builder — the merchant's whole website, in one place.
 *
 * <b>Why these were merged.</b> They shipped as two menu entries — «Website Builder» and
 * «Hjemmeside» — and a restaurant owner has one website, not two. The surviving name is the one
 * merchants, the onboarding checklist and the training material already use. Worse than the confusion: each
 * editor had its own colour picker writing to its own document, so a merchant could give their front
 * page and their ordering page different palettes and have no way to see they had done it. The look is
 * now set once, on the Hjemmeside tab, and publishing carries it to every location's ordering page.
 *
 * The two tabs are still two editors underneath, because they edit genuinely different things: a
 * per-merchant front page built from blocks, and a per-location ordering page whose content is the
 * merchant's catalogue. What they no longer do is disagree about what the place looks like.
 */
import SiteHome from "@/pages/settings/SiteHome.vue";
import SiteTheme from "@/pages/settings/SiteTheme.vue";

/** URL and code are English like every other route here; only what the merchant reads is Norwegian. */
const TABS = ["home", "ordering"];

export default {
  name: "SiteEditor",
  components: { SiteHome, SiteTheme },
  data() {
    return {
      tab: "home",
      /**
       * Bumped when the homepage publishes, which remounts the ordering tab.
       *
       * Publishing pushes the new theme onto every location's site_schema server-side. An ordering tab
       * still holding the schema it loaded before that would save the OLD theme back over it the next
       * time the merchant touched anything there — the change would appear to work and then quietly
       * undo itself.
       */
      orderingReload: 0
    };
  },
  created() {
    const wanted = String(this.$route.query.tab || "");
    if (TABS.includes(wanted)) this.tab = wanted;
  },
  methods: {
    go(tab) {
      if (!TABS.includes(tab) || tab === this.tab) return;
      this.tab = tab;
      // In the URL so a merchant can be sent straight to the right half, and so the browser's back
      // button does what they expect after they switch.
      this.$router.replace({ path: "/siteBuilder", query: { tab: tab } });
    }
  }
};
</script>

<style scoped>
.site-editor__title {
  margin: 0;
  padding: 14px 12px 2px;
}

.site-editor__lead {
  margin: 0 12px 12px;
  font-style: italic;
  color: var(--bo-muted);
}

.site-editor__tabs {
  display: flex;
  gap: 4px;
  padding: 0 12px;
  border-bottom: 1px solid var(--bo-line);
}

/*
  Which tab you are on has to be legible at a glance, so it is carried by FOUR signals rather than a
  one-shade background change: the brand edge along the top, the panel's own surface, ink instead of
  muted text, and the weight. The single shade was the whole tell before, and in dark mode the two
  surfaces are close enough that the tabs read as a pair of identical buttons.

  The brand edge is a transparent border on the inactive tabs, not a missing one — otherwise the label
  shifts up three pixels as you switch.
*/
.site-editor__tabs button {
  border: 1px solid var(--bo-line);
  border-top: 3px solid transparent;
  border-bottom: 0;
  background: var(--bo-surface-2);
  color: var(--bo-muted);
  border-radius: 8px 8px 0 0;
  padding: 8px 18px 9px;
  font-size: .92rem;
  cursor: pointer;
  transition: background .15s ease, color .15s ease;
  /* Sits on the container's own border, so the active tab reads as joined to the panel below it. */
  margin-bottom: -1px;
}

.site-editor__tabs button:hover:not(.active) {
  color: var(--bo-ink);
  background: var(--bo-surface);
}

.site-editor__tabs button:focus-visible {
  outline: 2px solid var(--bo-brand-ink);
  outline-offset: 2px;
}

.site-editor__tabs button.active {
  background: var(--bo-surface);
  border-top-color: var(--bo-brand-ink);
  color: var(--bo-ink);
  border-bottom: 1px solid var(--bo-surface);
  font-weight: 600;
}

/* The icon carries the state too, for anyone who reads shape before colour.
   `svg`, not `i`: Backoffice renders Font Awesome through <font-awesome-icon>, which emits an <svg>.
   There is no icon FONT loaded anywhere in this app, so `<i class="fa fa-…">` draws nothing at all. */
.site-editor__tabs button.active svg { color: var(--bo-brand-ink); }

.site-editor__note {
  margin: 12px 12px 0;
  font-size: .85rem;
  color: var(--bo-muted);
}

.site-editor__note a { color: var(--bo-brand-ink); }
</style>
